import { useContext, useMemo, useCallback, useState, useEffect } from 'react';
import { NavLink as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { SidebarContext } from 'src/contexts/SidebarContext';
import { useRole } from 'src/contexts/RoleContext/RoleContext';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from 'src/store/store';
import { signOut } from 'aws-amplify/auth';
import { logout } from 'src/store/reducers/authReducer';
import {
  LayoutDashboard, BedDouble, ClipboardList, MessageCircle, LifeBuoy,
  Stethoscope, HeartHandshake, UserMinus, MessageSquareWarning,
  Video, LogOut, UserCircle, ClipboardCheck, ScanLine,
} from 'lucide-react';
import { AnimatePresence } from 'framer-motion';
import { useThemeMode } from '../../../../theme/ThemeProvider';
import ApiServices from 'src/shared/Services/apiService';
import SignOutOverlay from '../../../../components/SignOutOverlay';


type NavItem = { label: string; to: string; icon: React.ReactNode; badge?: number | string };
type NavGroup = { items: NavItem[] };

function SidebarMenu() {
  const { closeSidebar, isCollapsed } = useContext(SidebarContext);
  const { role } = useRole();
  const { isDark } = useThemeMode();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const pk = useSelector((state: RootState) => state.auth.pk);
  const profile = useSelector((state: RootState) => state.auth.profile);
  const [pendingCount, setPendingCount] = useState<number | undefined>(undefined);
  const [pendingReportsCount, setPendingReportsCount] = useState<number | undefined>(undefined);
  const [tooltip, setTooltip] = useState<{ label: string; y: number } | null>(null);
  const [isSigningOut, setIsSigningOut] = useState(false);

  const isAdmin = role === 'admin';

  const userName = isAdmin
    ? (profile?.AdminName || '')
    : (() => {
      const patientName = profile?.PatientName || profile?.name || '';
      const fn = profile?.FirstName || patientName.split(' ')[0] || '';
      const ln = profile?.LastName || (patientName.split(' ').slice(1).join(' ')) || '';
      return fn && ln ? `${fn} ${ln}`.trim() : fn || patientName || '';
    })();

  const userEmail = profile?.Email || '';

  const nameParts = userName.trim().split(' ').filter(Boolean);
  const initials = nameParts.length >= 2
    ? `${nameParts[0][0]}${nameParts[nameParts.length - 1][0]}`.toUpperCase()
    : nameParts[0]?.[0]?.toUpperCase() || '?';

  useEffect(() => {
    if (role !== 'admin' && role !== 'nurse') return;
    const fetchCount = async () => {
      try {
        const res = await ApiServices.getPendingQueriesCount();
        setPendingCount(res?.count > 0 ? res.count : undefined);
      } catch (error) {
        console.error('Failed to fetch pending queries count:', error);
      }
    };
    fetchCount();
    const interval = setInterval(fetchCount, 30000);
    return () => clearInterval(interval);
  }, [role]);

  useEffect(() => {
    if (role !== 'admin' && role !== 'nurse') return;
    const fetchReportsCount = async () => {
      try {
        const res = await ApiServices.getPatientReports(null, 'pending_review');
        const count = (res?.reports ?? []).length;
        setPendingReportsCount(count > 0 ? count : undefined);
      } catch {
        // silently ignore — badge just won't show on error
      }
    };
    fetchReportsCount();
    const interval = setInterval(fetchReportsCount, 60000);
    // The Pending Reports page announces approvals and send-backs right away.
    window.addEventListener('pendingReportsChanged', fetchReportsCount);
    return () => {
      clearInterval(interval);
      window.removeEventListener('pendingReportsChanged', fetchReportsCount);
    };
  }, [role]);

  const menuGroups: Record<string, NavGroup[]> = useMemo(() => ({
    patient: [
      {
        items: [
          { label: 'Dashboard', to: '/', icon: <LayoutDashboard size={20} /> },
          { label: 'My Profile', to: `patient-details/PersonalInformation?patientId=${pk}`, icon: <UserCircle size={20} /> },
          { label: 'My Report', to: '/patient/reports', icon: <ClipboardList size={20} /> },
        ],
      },
      {
        items: [
          { label: 'Messages', to: '/patient/queries', icon: <MessageCircle size={20} /> },
          { label: 'Video Calls', to: '/video-consultation', icon: <Video size={20} /> },
          { label: 'Help Center', to: '/help', icon: <LifeBuoy size={20} /> },
        ],
      },
    ],
    doctor: [
      {
        items: [
          { label: 'Dashboard', to: '/', icon: <LayoutDashboard size={20} /> },
          { label: 'Patients', to: '/patients', icon: <BedDouble size={20} /> },
          { label: 'Video Calls', to: '/video-consultation', icon: <Video size={20} /> },
        ],
      },
      {
        items: [
          { label: 'Help Center', to: '/help', icon: <LifeBuoy size={20} /> },
        ],
      },
    ],
    admin: [
      {
        items: [
          { label: 'Dashboard', to: '/', icon: <LayoutDashboard size={20} /> },
          { label: 'Patients', to: '/patients', icon: <BedDouble size={20} /> },
          { label: 'Specialists', to: '/doctors', icon: <Stethoscope size={20} /> },
          { label: 'Coordinators', to: '/nurses', icon: <HeartHandshake size={20} /> },
          { label: 'Unassigned', to: '/unassigned-patients', icon: <UserMinus size={20} /> },
          { label: 'Queries', to: '/queries', icon: <MessageSquareWarning size={20} />, badge: pendingCount },
          { label: 'Reports', to: '/pending-reports', icon: <ClipboardCheck size={20} />, badge: pendingReportsCount },
          { label: 'Video Calls', to: '/video-consultation', icon: <Video size={20} /> },

        ],
      },
    ],
    nurse: [
      {
        items: [
          { label: 'Dashboard', to: '/', icon: <LayoutDashboard size={20} /> },
          { label: 'Patients', to: '/patients', icon: <BedDouble size={20} /> },
          { label: 'Unassigned', to: '/unassigned-patients', icon: <UserMinus size={20} /> },
          { label: 'Queries', to: '/queries', icon: <MessageSquareWarning size={20} />, badge: pendingCount },
          { label: 'Reports', to: '/pending-reports', icon: <ClipboardCheck size={20} />, badge: pendingReportsCount },
          { label: 'Video Calls', to: '/video-consultation', icon: <Video size={20} /> },
        ],
      },
    ],
  }), [pk, pendingCount, pendingReportsCount]);

  const groups = menuGroups[role] || [];

  const isActive = useCallback((to: string) => {
    if (to === '/') return location.pathname === '/';
    const basePath = to.split('?')[0];
    const normalizedTo = basePath.startsWith('/') ? basePath : `/${basePath}`;
    if (normalizedTo.startsWith('/patient-details')) return location.pathname.startsWith('/patient-details');
    if (normalizedTo === '/patients') {
      if (location.pathname.startsWith('/patient-details')) return true;
      if (location.pathname.startsWith('/imaging-studies') && location.search.includes('patientId=')) return true;
    }
    return location.pathname.startsWith(normalizedTo);
  }, [location.pathname, location.search]);

  const handleSignOut = async () => {
    setIsSigningOut(true);
    try {
      await signOut();
      localStorage.removeItem('stepper_completed');
      dispatch(logout());
      navigate('/');
    } catch (error) {
      console.error('Error signing out:', error);
      setIsSigningOut(false);
    }
  };

  return (
    <nav className={['flex flex-col flex-1 pt-2 pb-2 relative', isCollapsed ? 'px-2' : 'px-2.5'].join(' ')}>
      {/* Navigation groups */}
      <div className="flex-1 space-y-0">
        {groups.map((group, gi) => (
          <div key={gi}>
            {gi > 0 && <div className="my-2" />}
            <ul className="flex flex-col gap-1">
              {group.items.map((item) => {
                const active = isActive(item.to);
                return (
                  <li key={item.to} className="relative">
                    <RouterLink
                      to={item.to}
                      onClick={closeSidebar}
                      onMouseEnter={e => {
                        if (isCollapsed) {
                          const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                          setTooltip({ label: item.label, y: rect.top + rect.height / 2 });
                        }
                      }}
                      onMouseLeave={() => setTooltip(null)}
                      className={[
                        'relative flex items-center w-full rounded-xl',
                        isCollapsed ? 'justify-center px-0 py-[9px]' : 'gap-3 px-2.5 py-[9px]',
                        'text-[14.5px] leading-tight min-h-[42px]',
                        'transition-[padding,background-color,color,box-shadow] duration-[280ms] ease-[cubic-bezier(0.22,1,0.36,1)]',
                        'group/nav select-none',
                        active
                          ? isCollapsed
                            ? '' // no background in collapsed — icon pill carries active state
                            : isDark
                              ? 'font-semibold text-white bg-gradient-to-br from-brand-500/[0.20] to-brand-500/[0.06] ring-1 ring-inset ring-brand-400/25'
                              : 'font-semibold text-brand-700 bg-gradient-to-br from-brand-500/[0.10] to-brand-500/[0.03] ring-1 ring-inset ring-brand-500/[0.15]'
                          : isDark
                            ? 'font-medium text-slate-300 hover:text-white'
                            : 'font-medium text-slate-600 hover:text-slate-900',
                      ].join(' ')}
                    >
                      {/* Active left bar — thicker for better visibility */}
                      {active && (
                        <span
                          className={[
                            'absolute left-0 top-[9px] bottom-[9px] w-[3.5px] rounded-r-full bg-gradient-to-b from-brand-300 via-brand-500 to-brand-700 shadow-dark-active-bar',
                            'transition-opacity duration-[280ms] ease-[cubic-bezier(0.22,1,0.36,1)]',
                            isCollapsed ? 'opacity-0' : 'opacity-100',
                          ].join(' ')}
                        />
                      )}

                      {!active && (
                        <span className={[
                          'absolute inset-0 rounded-xl opacity-0 group-hover/nav:opacity-100 transition-opacity duration-150',
                          isDark ? 'bg-white/[0.06]' : 'bg-slate-100/60',
                        ].join(' ')} />
                      )}

                      <span
                        className={[
                          'relative flex items-center justify-center flex-shrink-0 transition-all duration-200',
                          isCollapsed
                            ? 'w-[38px] h-[38px] rounded-[11px]'
                            : 'w-[32px] h-[32px] rounded-[9px]',
                          active
                            ? isDark
                              ? 'bg-brand-500/30 text-brand-300 ring-1 ring-inset ring-brand-400/25'
                              : 'bg-brand-500/[0.12] text-brand-600 ring-1 ring-inset ring-brand-500/[0.15]'
                            : isDark
                              ? 'bg-white/[0.05] text-slate-400 group-hover/nav:bg-white/[0.09] group-hover/nav:text-slate-200'
                              : 'bg-slate-100 text-slate-500 group-hover/nav:bg-slate-100 group-hover/nav:text-brand-600',
                        ].join(' ')}
                      >
                        {item.icon}
                      </span>

                      {/* Label + badge pill — collapse as one unit via max-width+opacity CSS */}
                      <span
                        className={[
                          'flex items-center flex-1 min-w-0 gap-2 overflow-hidden',
                          'transition-[opacity,max-width] duration-[280ms] ease-[cubic-bezier(0.22,1,0.36,1)]',
                          isCollapsed ? 'opacity-0 max-w-0' : 'opacity-100 max-w-[165px]',
                        ].join(' ')}
                        aria-hidden={isCollapsed}
                      >
                        <span className="whitespace-nowrap tracking-[-0.01em] leading-snug flex-1 min-w-0 overflow-hidden">
                          {item.label}
                        </span>
                        {item.badge !== undefined && (
                          <span className="flex-shrink-0 flex items-center justify-center min-w-[19px] h-[19px] px-1.5 text-micro-xs font-bold text-white bg-red-500 rounded-full shadow-sm shadow-red-500/25 ring-[1.5px] ring-white dark:ring-surface-dark">
                            {item.badge}
                          </span>
                        )}
                      </span>

                      {/* Badge dot — collapsed only, CSS opacity crossfade */}
                      {item.badge !== undefined && (
                        <span
                          className={[
                            'absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-red-500 ring-[1.5px] ring-white dark:ring-dark-chrome-sidebar',
                            'transition-opacity duration-[280ms] ease-[cubic-bezier(0.22,1,0.36,1)]',
                            isCollapsed ? 'opacity-100' : 'opacity-0 pointer-events-none',
                          ].join(' ')}
                        />
                      )}


                    </RouterLink>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </div>

      {/* ── Bottom: User strip — slim, clean, premium ── */}
      <div
        className={[
          'mt-auto pt-2',
          isDark ? 'border-t border-white/[0.07]' : 'border-t border-slate-100',
        ].join(' ')}
      >
        <div
          className={[
            'relative flex items-center rounded-xl',
            'transition-[padding,opacity] duration-[280ms] ease-[cubic-bezier(0.22,1,0.36,1)]',
            isCollapsed ? 'justify-center px-0 py-2' : 'gap-2.5 px-2.5 py-2',
            isDark
              ? 'hover:bg-white/[0.05]'
              : 'hover:bg-slate-50',
            'cursor-default group/userbar',
          ].join(' ')}
        >
          {/* Avatar — 32px, compact */}
          <div className="relative flex-shrink-0">
            <div
              className={[
                'flex items-center justify-center rounded-full font-bold text-white select-none',
                'bg-gradient-to-br from-brand-500 to-brand-700',
                isCollapsed ? 'w-8 h-8 text-[11px]' : 'w-8 h-8 text-[11px]',
              ].join(' ')}
            >
              {initials}
            </div>
            {/* Online dot */}
            <span
              className={[
                'absolute -bottom-px -right-px w-2 h-2 rounded-full bg-emerald-500',
                'ring-[1.5px]',
                isDark ? 'ring-[#0e1830]' : 'ring-white',
              ].join(' ')}
            />
          </div>

          <div
            className={[
              'flex-1 min-w-0 overflow-hidden',
              'transition-[opacity,max-width] duration-[280ms] ease-[cubic-bezier(0.22,1,0.36,1)]',
              isCollapsed ? 'opacity-0 max-w-0' : 'opacity-100 max-w-[130px]',
            ].join(' ')}
            aria-hidden={isCollapsed}
          >
            <div className="flex items-baseline gap-1.5 min-w-0">
              <p
                className={[
                  'text-[13px] font-semibold truncate leading-none',
                  isDark ? 'text-slate-100' : 'text-slate-700',
                ].join(' ')}
              >
                {userName || ''}
              </p>
            </div>
            <p
              className={[
                'text-[11px] leading-none mt-[3px] truncate font-medium',
                isDark ? 'text-slate-500' : 'text-slate-400',
              ].join(' ')}
              title={userEmail}
            >
              {userEmail}
            </p>
          </div>

          <div
            className={[
              'overflow-hidden flex-shrink-0',
              'transition-[opacity,max-width] duration-[200ms] ease-[cubic-bezier(0.22,1,0.36,1)]',
              isCollapsed ? 'opacity-0 max-w-0 pointer-events-none' : 'opacity-0 group-hover/userbar:opacity-100 max-w-[28px]',
            ].join(' ')}
          >
            <button
              onClick={handleSignOut}
              className={[
                'w-7 h-7 flex items-center justify-center rounded-lg transition-colors duration-150',
                isDark
                  ? 'text-slate-600 hover:text-red-400 hover:bg-red-500/10'
                  : 'text-slate-400 hover:text-red-500 hover:bg-red-50',
              ].join(' ')}
              title="Sign out"
              aria-label="Sign out"
              tabIndex={isCollapsed ? -1 : 0}
            >
              <LogOut size={13} />
            </button>
          </div>
        </div>
      </div>

      {isCollapsed && tooltip && (
        <div
          className={[
            'fixed left-[82px] z-[9999] pointer-events-none',
            'flex items-center gap-1.5',
          ].join(' ')}
          style={{ top: tooltip.y - 16 }}
        >
          {/* Left caret */}
          <span
            className={[
              'w-0 h-0 flex-shrink-0',
              'border-t-[5px] border-b-[5px] border-r-[6px]',
              'border-t-transparent border-b-transparent',
              isDark ? 'border-r-dark-chrome-dropdown' : 'border-r-white',
            ].join(' ')}
            style={{ filter: isDark ? 'none' : 'drop-shadow(-1px 0 0 rgb(226 232 240))' }}
          />
          <div
            className={[
              'px-2.5 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap',
              'shadow-lg border',
              isDark
                ? 'bg-dark-chrome-dropdown border-dark-border text-slate-100'
                : 'bg-white border-slate-200 text-slate-700 shadow-md',
            ].join(' ')}
          >
            {tooltip.label}
          </div>
        </div>
      )}
      <AnimatePresence>
        {isSigningOut && <SignOutOverlay />}
      </AnimatePresence>
    </nav>
  );
}

export default SidebarMenu;
